import BodyPartSelectModal from "$/components/BodyPartSelectModal";
import ChipSelect from "$/components/ChipSelect";
import ColorPicker from "$/components/ColorPicker";
import CreationDetailSelector from "$/components/CreationDetailSelector";
import CreationDetailTempBanner from "$/components/CreationDetailTempBanner";
import CreationDetailToolbar from "$/components/CreationDetailToolbar";
import CreationImage from "$/components/CreationImage";
import CreditProgress from "$/components/CreditProgress";
import ExpiredOverlay from "$/components/ExpiredOverlay";
import InkProgress from "$/components/InkProgress";
import MaskDrawingModal from "$/components/MaskDrawingModal";
import PricingModal from "$/components/PricingModal";
import PromptField from "$/components/PromptField";
import SectionHeader from "$/components/SectionHeader";
import SocialShareModal from "$/components/SocialShareModal";
import StyleSelectModal from "$/components/StyleSelectModal";
import UpgradeBanner from "$/components/UpgradeBanner";
import ProBadge from "$/components/icons/ProBadge";
import commonConstants from "$/data/common";
import analytics from "$/util/analytics";
import { useAuth } from "$/util/auth";
import { gradientGrainStyling } from "$/util/background";
import { getCreationBucket, isUpload } from "$/util/creation_util";
import {
  createCreation,
  getImageUrl,
  refreshUser,
  subscribeCreation,
  updateCreation,
  useCreation,
} from "$/util/db";
import { generateRandomIdea } from "$/util/ideas_util";
import constants from "@blackink/core/categoryConstants";
import { getCreationUrl } from "@blackink/core/creation/util";
import { ArrowBack, Category } from "@mui/icons-material";
import BodyIcon from "@mui/icons-material/AccessibilityNew";
import MinorDetailIcon from "@mui/icons-material/Adjust";
import CloseIcon from "@mui/icons-material/Close";
import ColorIcon from "@mui/icons-material/ColorizeSharp";
import MajorDetailIcon from "@mui/icons-material/Deblur";
import DoubleArrowIcon from "@mui/icons-material/DoubleArrowRounded";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import InfoIcon from "@mui/icons-material/Info";
import DifferenceIcon from "@mui/icons-material/Iso";
import LineWeightIcon from "@mui/icons-material/LineWeight";
import ModelTrainingIcon from "@mui/icons-material/ModelTraining";
import ShareIcon from "@mui/icons-material/Share";
import StyleIcon from "@mui/icons-material/Style";
import TextFieldsIcon from "@mui/icons-material/TextFields";
import MediumDetailIcon from "@mui/icons-material/Tonality";
import VisibilityIcon from "@mui/icons-material/VisibilityOffOutlined";
import { LoadingButton } from "@mui/lab";
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useSearchParams } from "react-router-dom";
import supabase from "../../util/supabase";

interface LocationState {
  creationId?: string;
  variant?: number;
  prompt?: string;
  style?: string;
  bodyPart?: string;
  customShapeMask: Array<[number, number]>;
  customShapeImg: HTMLImageElement;
  autorun?: boolean;
}

function CreateDesignTool(props) {
  const auth = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const desktopScreen = useMediaQuery(theme.breakpoints.up("md"));
  const previewRef = useRef(null);

  const {
    creationId: remixCreationId,
    variant: remixIdx,
    prompt: inputPrompt,
    style: inputStyle,
    bodyPart: inputBodyPart,
    customShapeImg: inputCustomShapeImgSrc,
    customShapeMask: inputCustomShapeMask,
    autorun,
  }: LocationState = location.state ?? {};

  const remixCreation = useCreation(remixCreationId);

  const [pending, setPending] = useState(false);
  const [formAlert, setFormAlert] = useState(null);
  const [modifiers, setModifiers] = useState([]);
  const [detailComplexity, setDetailComplexity] = useState(2);
  const [creation, setCreation] = useState(null);
  const [openShareModal, setOpenShareModal] = useState(false);
  const [variant, setVariant] = useState(0);
  const [searchParams, setSearchParams] = useSearchParams();

  const [formValues, setFormValues] = useState({
    prompt: inputPrompt ?? searchParams.get("q") ?? "",
    style: inputStyle ?? "black-grey",
    bodyPart: inputBodyPart ?? "square",
    public: true,
    params: {
      template: inputCustomShapeMask ?? null,
    } as {
      // meta params
      colors?: string[];
      detail?: number;
      complexity?: number;
      inner_line_weight?: number;
      outer_line_weight?: number;
      remix_strength?: string;
      // text params
      text_content?: string;
      // internal params
      white_level?: number;
      black_level?: number;
      strength?: number;
      num_inference_steps?: number;
      shift?: number;
      template?: Array<[number, number]>;
    },
  });
  const [errors, setErrors] = useState({} as Record<string, { message: string }>);
  const [advancedOptionsOpen, setAdvancedOptionsOpen] = useState(false);
  const [styleSelectOpen, setStyleSelectOpen] = useState(false);
  const [bodyPartSelectOpen, setBodyPartSelectOpen] = useState(false);
  const [customShapeToolOpen, setCustomShapeToolOpen] = useState(false);
  const [customShapeImg, setCustomShapeImg] = useState(
    inputCustomShapeImgSrc
      ? Object.assign(document.createElement("img"), {
          src: inputCustomShapeImgSrc,
          alt: "Mask Image",
          width: 512,
          height: 512,
        })
      : null,
  );
  const [shouldCreate, setShouldCreate] = useState(false);
  const [autoRunComplete, setAutoRunComplete] = useState(false);

  const creditsDepleted = auth.user && auth.user.credits <= 0 && !auth.isProUser;
  // default credit is now 10 earlier it was 5
  // so to show the upgrade banner, free credit use is now limited to 3 = 10(total free credits)-7(used credits)
  const anonCreditsDepleted =
    auth.isAnonymousUser && auth.user && auth.user.credits <= 0;
  const isUploadedRemix = isUpload(remixCreation.data);

  const handleCreationEvent = (creationEvent) => {
    setCreation(creationEvent.new);
    if (
      creationEvent.new.status === "SUCCESS" ||
      creationEvent.new.status === "ERROR"
    ) {
      setPending(false);
      auth.user && refreshUser(auth.user.id);
      supabase.removeAllChannels();
    }
  };

  const onChange = (name, value) => {
    if (value === formValues[name]) return;
    if (errors) {
      setErrors({});
    }
    setFormValues({ ...formValues, [name]: value });
  };

  const onChangeParams = (changes) => {
    if (errors) {
      setErrors({});
    }
    setFormValues({ ...formValues, params: { ...formValues.params, ...changes } });
  };

  const unsetParams = (keys) => {
    const newParams = { ...formValues.params };
    keys.forEach((key) => delete newParams[key]);
    setFormValues({ ...formValues, params: newParams });
  };

  const validateForm = () => {
    if (!formValues.prompt) {
      setErrors({ prompt: { message: "Please enter an idea (ex. Skull and Roses)" } });
      return false;
    }
    setErrors({});
    return true;
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    if (!auth.user) {
      // Join anonymously, passthrough user because hooks won't have updated user yet
      await auth.signinAnonymously();
    }

    setPending(true);
    setShouldCreate(true);
  };

  const runCreate = async () => {
    if (!auth.user) {
      // User should exist at least as an anonymous user
      setPending(false);
      return;
    }
    setPending(true);

    // Deep copy form values
    const requestData = JSON.parse(JSON.stringify(formValues));
    requestData.modifiers = modifiers;
    requestData.style = [requestData.style];

    if (remixCreation && remixCreation.data) {
      requestData.params = {
        ...requestData.params,
        init_image: getImageUrl(
          remixCreation.data.imagePaths[remixIdx],
          getCreationBucket(remixCreation.data),
        ),
        remix_is_upload: isUploadedRemix,
        remix_is_upload_color: isUploadedRemix && remixIdx === 0,
      };
    }

    if (requestData.params.text_content) {
      requestData.prompt = `"${requestData.params.text_content}" ${requestData.prompt}`;
    }

    const bodyPart = constants.BODY_PART_DESC[requestData.bodyPart];
    requestData.params = {
      ...requestData.params,
      height: bodyPart.height,
      width: bodyPart.width,
    };

    try {
      const creation = await (props.id
        ? updateCreation(props.id, requestData)
        : createCreation({ owner: auth.user.id, ...requestData }));

      setCreation(creation);

      subscribeCreation(creation.id, handleCreationEvent);

      // Scroll to the preview area
      previewRef.current?.scrollIntoView({ behavior: "smooth", block: "center" });

      analytics.track("create", {
        style: requestData.style,
        bodyPart: requestData.bodyPart,
        public: requestData.public,
        remix: !!remixCreationId,
        customShape: customShapeImg ? true : false,
      });
    } catch (error) {
      // Hide pending indicator
      setPending(false);
      // Show error alert message
      setFormAlert({
        type: "error",
        message: error.message,
      });
    }
  };

  const creationReady =
    creation && creation.status === "SUCCESS" && creation.imagePaths;

  useEffect(() => {
    if (remixCreation && remixCreation.data) {
      const {
        style,
        bodyPart,
        prompt,
        public: publicValue,
        params: remixParams,
      } = remixCreation.data;

      if (isUploadedRemix) {
        setFormValues((prevValues) => ({
          ...prevValues,
          prompt: inputPrompt ?? prompt,
          style: remixIdx === 0 ? "blackwork" : "sketch",
          bodyPart: inputBodyPart ?? "square",
          public: auth.isProUser ? publicValue : true,
          params: {},
        }));
      } else {
        setFormValues((prevValues) => ({
          ...prevValues,
          prompt: inputPrompt ?? prompt,
          style: style[0],
          bodyPart: inputBodyPart ?? bodyPart ?? "square",
          public: auth.isProUser ? publicValue : true,
          params: remixParams,
        }));
      }
    }
  }, [
    remixCreation.data,
    auth.isProUser,
    isUploadedRemix,
    inputPrompt,
    inputBodyPart,
    remixIdx,
  ]);

  useEffect(() => {
    if (inputPrompt !== undefined && inputPrompt !== formValues.prompt) {
      setFormValues((prev) => ({ ...prev, prompt: inputPrompt }));
    }
  }, [inputPrompt]);

  useEffect(() => {
    if (inputBodyPart !== undefined && inputBodyPart !== formValues.bodyPart) {
      setFormValues((prev) => ({ ...prev, bodyPart: inputBodyPart }));
    }
  }, [inputBodyPart]);

  useEffect(() => {
    if (remixCreationId) {
      setAdvancedOptionsOpen(true);
      // Scroll to top
      window.scrollTo(0, 0);
    } else if (desktopScreen && auth.isProUser) {
      setAdvancedOptionsOpen(true);
    } else if (!desktopScreen) {
      setAdvancedOptionsOpen(false);
    }
  }, [remixCreationId, remixIdx, desktopScreen, auth.isProUser]);

  useEffect(() => {
    if (shouldCreate && auth.user) {
      (async () => {
        try {
          await runCreate();
        } catch (error) {
          setPending(false);
          console.error("Failed to create", error);
        }
      })();
      setShouldCreate(false); // Reset the flag after running `runCreate`
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [shouldCreate, auth.user]);

  useEffect(() => {
    const autorunProcess = async () => {
      if (autorun && !autoRunComplete) {
        if (!validateForm()) {
          return;
        }

        if (!auth.user) {
          // Join anonymously, passthrough user because hooks won't have updated user yet
          await auth.signinAnonymously();
        }

        if (creditsDepleted || anonCreditsDepleted) {
          setPending(false);
          return;
        }

        setPending(true);
        setShouldCreate(true);
        setAutoRunComplete(true);
        // Set autorun state to false
        navigate(location.pathname, { state: { ...location.state, autorun: false } });
      }
    };

    autorunProcess();
  }, [
    auth,
    autorun,
    autoRunComplete,
    validateForm,
    creditsDepleted,
    anonCreditsDepleted,
  ]);

  React.useEffect(() => {
    if (searchParams.get('o') === 'bp') {
      setBodyPartSelectOpen(true);
    }
  }, [searchParams]);

  return (
    <Box flexGrow={1} mb={2}>
      {formAlert && (
        <Box mb={4}>
          <Alert severity={formAlert.type}>{formAlert.message}</Alert>
        </Box>
      )}
      {creditsDepleted && auth.user && (auth.user.userType || auth.isAnonymousUser) && (
        <PricingModal />
      )}

      <Grid
        container
        spacing={2}
        rowSpacing={0}
        sx={{
          pt: 2,
          // Remove padding on the grid items
          "& > .MuiGrid-item": {
            pt: 1,
          },
        }}
      >
        <Grid item xs={12} md={5} lg={4}>
          {props.onBack ? (
            <Button
              onClick={props.onBack}
              variant="text"
              color="inherit"
              startIcon={<ArrowBack />}
            >
              Start Over
            </Button>
          ) : (
            <SectionHeader title="AI Tattoo Generator" size={5} seoSize={1} />
          )}

          {!auth.isProUser && (
            <CreditProgress
              credits={auth.user ? auth.user.credits : commonConstants.FREE_CREDITS}
            />
          )}
          <Box position="relative" pt={1}>
            {(creditsDepleted || anonCreditsDepleted) && <ExpiredOverlay />}
            <form onSubmit={onSubmit}>
              <Stack sx={{ position: "relative" }}>
                <PromptField
                  name="prompt"
                  label="Tattoo Idea"
                  autoFocus
                  disabled={creditsDepleted || anonCreditsDepleted}
                  value={formValues.prompt}
                  onChange={(e, newValue) => {
                    // Disable new lines in the input field
                    const newPrompt = newValue.replace(/(\r\n|\n|\r)/gm, "");
                    if (newPrompt === formValues.prompt) return;
                    onChange("prompt", newPrompt);
                  }}
                  onBlur={() => {}}
                  onRandom={() => {
                    const randomIdea = generateRandomIdea(auth.isProUser);

                    if (customShapeImg) {
                      // Clear custom shape
                      setCustomShapeImg(null);
                    }

                    setFormValues({
                      ...randomIdea,
                      public: formValues.public,
                      params: {},
                    });
                    setDetailComplexity(2);
                    analytics.track("random_idea");
                  }}
                  textFieldProps={{
                    error: errors.prompt ? true : false,
                    helperText: (errors.prompt && errors.prompt.message) || "",
                  }}
                />
                {formValues.params?.text_content !== undefined ? (
                  <TextField
                    fullWidth
                    size="small"
                    value={formValues.params.text_content}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                      const newText = e.target.value;
                      if (newText.length <= 30) {
                        onChangeParams({
                          text_content: newText,
                        });
                      }
                    }}
                    placeholder="Add text to your tattoo"
                    InputProps={{
                      startAdornment: (
                        <InputAdornment position="start">
                          <TextFieldsIcon />
                        </InputAdornment>
                      ),
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="small"
                            onClick={() => {
                              unsetParams(["text_content"]);
                            }}
                            edge="end"
                          >
                            <CloseIcon fontSize="small" />
                          </IconButton>
                        </InputAdornment>
                      ),
                    }}
                    disabled={creditsDepleted || anonCreditsDepleted}
                    sx={{ mb: 1, mt: 0.5 }}
                    helperText={`${formValues.params.text_content.length}/30 characters`}
                  />
                ) : (
                  <Button
                    onClick={() => {
                      onChangeParams({
                        text_content: "",
                      });
                    }}
                    startIcon={<TextFieldsIcon />}
                    sx={{
                      justifyContent: "flex-start",
                      mb: 1,
                      mt: 0.5,
                      pl: 2,
                    }}
                    size="small"
                    disabled={creditsDepleted || anonCreditsDepleted}
                  >
                    Add Text
                    <Chip
                      label="New"
                      color="primary"
                      size="small"
                      sx={{ ml: 1, fontSize: 10 }}
                    />
                  </Button>
                )}
                <Stack
                  direction={{ xs: "column", sm: "row", md: "column" }}
                  spacing={1}
                >
                  <FormControl variant="outlined" fullWidth>
                    <TextField
                      id="style-select"
                      label="Style"
                      name="style"
                      disabled={creditsDepleted || anonCreditsDepleted}
                      InputProps={{
                        readOnly: true,
                        // Style image
                        startAdornment: (
                          <img
                            src={`/images/styles/${formValues.style}.webp`}
                            alt={formValues.style}
                            style={{
                              width: 32,
                              height: 32,
                              marginRight: 8,
                              borderRadius: 4,
                              border: `1px solid ${theme.palette.divider}`,
                            }}
                          />
                        ),
                        endAdornment: <StyleIcon />,
                      }}
                      sx={{
                        "& .MuiInputBase-root": {
                          cursor: "pointer",
                        },
                        input: {
                          cursor: "pointer",
                        },
                        // Set focus state if the select is open
                        "& .MuiOutlinedInput-root.Mui-focused": {
                          borderColor: theme.palette.primary.main,
                        },
                      }}
                      onChange={(e) => onChange("style", e.target.value)}
                      onClick={() => setStyleSelectOpen(true)}
                      value={constants.CATEGORY_DESC[formValues.style].name}
                    />
                    {styleSelectOpen && (
                      <StyleSelectModal
                        style={formValues.style}
                        open={styleSelectOpen}
                        onClose={(style) => {
                          // Close the modal
                          setStyleSelectOpen(false);
                          // Set the style
                          if (style) {
                            onChange("style", style);
                          }
                        }}
                      />
                    )}
                  </FormControl>

                  <FormControl variant="outlined" fullWidth>
                    {customShapeImg ? (
                      <TextField
                        id="body-part-select"
                        label="Body Part / Placement"
                        name="bodyPart"
                        disabled={
                          creditsDepleted || anonCreditsDepleted || !!remixCreationId
                        }
                        InputProps={{
                          readOnly: true,
                          endAdornment: (
                            <InputAdornment position="end">
                              <IconButton
                                onClick={(evt) => {
                                  evt.stopPropagation(); // Prevents modal from opening
                                  setCustomShapeImg(null);
                                  unsetParams(["template"]);
                                }}
                              >
                                <CloseIcon />
                              </IconButton>
                            </InputAdornment>
                          ),
                          startAdornment: (
                            <img
                              src={customShapeImg.src}
                              alt="Custom Shape"
                              style={{
                                width: 32,
                                height: 32,
                                marginRight: 8,
                                borderRadius: 4,
                                border: `1px solid ${theme.palette.divider}`,
                              }}
                            />
                          ),
                        }}
                        sx={{
                          "& .MuiInputBase-root": {
                            cursor: "pointer",
                          },
                          input: {
                            cursor: "pointer",
                          },
                          // Set focus state if the select is open
                          "& .MuiOutlinedInput-root.Mui-focused": {
                            borderColor: theme.palette.primary.main,
                          },
                        }}
                        onClick={() => setCustomShapeToolOpen(true)}
                        value={"Custom Shape"}
                      />
                    ) : (
                      <TextField
                        id="body-part-select"
                        label="Body Part / Placement"
                        name="bodyPart"
                        disabled={
                          creditsDepleted || anonCreditsDepleted || !!remixCreationId
                        }
                        InputProps={{
                          readOnly: true,
                          endAdornment: <BodyIcon />,
                          startAdornment: (
                            <img
                              src={`/images/bodyParts/${formValues.bodyPart}.png`}
                              alt={formValues.bodyPart}
                              style={{
                                width: 32,
                                height: 32,
                                marginRight: 8,
                                borderRadius: 4,
                                border: `1px solid ${theme.palette.divider}`,
                              }}
                            />
                          ),
                        }}
                        sx={{
                          "& .MuiInputBase-root": {
                            cursor: "pointer",
                          },
                          input: {
                            cursor: "pointer",
                          },
                          // Set focus state if the select is open
                          "& .MuiOutlinedInput-root.Mui-focused": {
                            borderColor: theme.palette.primary.main,
                          },
                        }}
                        onChange={(e) => onChange("bodyPart", e.target.value)}
                        onClick={() => setBodyPartSelectOpen(true)}
                        value={constants.BODY_PART_DESC[formValues.bodyPart].name}
                      />
                    )}
                    {!customShapeImg && !remixCreationId && (
                      <Button
                        onClick={() => setCustomShapeToolOpen(true)}
                        startIcon={<Category />}
                        sx={{ justifyContent: "flex-start", pl: 2.5 }}
                        size="small"
                      >
                        Custom Shape
                      </Button>
                    )}
                    {bodyPartSelectOpen && (
                      <BodyPartSelectModal
                        bodyPart={searchParams.get("t") ?? formValues.bodyPart}
                        open={bodyPartSelectOpen}
                        onClose={(bodyPart) => {
                          // Close the modal
                          setBodyPartSelectOpen(false);
                          // Set the bodyPart
                          if (bodyPart) {
                            onChange("bodyPart", bodyPart);
                          }
                        }}
                      />
                    )}
                    <MaskDrawingModal
                      open={customShapeToolOpen}
                      img={customShapeImg}
                      onClose={(mask, img, canceled = false) => {
                        setCustomShapeToolOpen(false);
                        if (canceled) {
                          console.log("Canceled custom shape drawing");
                          return;
                        }
                        console.log("Custom shape drawn", mask.length);

                        // Don't use onChange funcs because they will conflict calling parallel
                        setFormValues({
                          ...formValues,
                          bodyPart: "square",
                          params: {
                            ...formValues.params,
                            template: mask,
                            strength: 0.85,
                          },
                        });
                        setCustomShapeImg(img);
                      }}
                    />
                  </FormControl>
                </Stack>
                <Grid item xs={12} mt={1}>
                  <Accordion
                    variant="outlined"
                    sx={{
                      backgroundColor: "rgba(0, 0, 0, 0.05)",
                      position: "relative",
                      overflow: "visible",
                    }}
                    expanded={advancedOptionsOpen}
                    onChange={() => setAdvancedOptionsOpen(!advancedOptionsOpen)}
                    disableGutters
                  >
                    <AccordionSummary
                      expandIcon={<ExpandMoreIcon />}
                      aria-controls="panel1-content"
                      id="panel1-header"
                      sx={{
                        "& .MuiAccordionSummary-content": {
                          justifyContent: "space-between",
                          display: "flex",
                        },
                      }}
                    >
                      Advanced Options
                      {!auth.isProUser && (
                        <Chip
                          label={
                            <>
                              <ProBadge
                                color={theme.palette.primary.contrastText}
                                height={16}
                                sx={{ ml: -0.5, mb: -0.4 }}
                              />
                              Pro Only
                            </>
                          }
                          color="primary"
                          size="small"
                          sx={{ mx: 1 }}
                        />
                      )}
                    </AccordionSummary>
                    <AccordionDetails>
                      <Stack direction="column" spacing={2}>
                        {!auth.isProUser && (
                          <Alert
                            severity="info"
                            color="primary"
                            sx={{
                              padding: "0px 8px",
                              border: "1px solid #ccc",
                              "& .MuiAlert-message": {
                                padding: "4px 0",
                                alignItems: "center",
                                display: "flex",
                                justifyContent: "space-between",
                                flex: 1,
                                fontSize: "0.7rem",
                              },
                            }}
                          >
                            Advanced options are available to Pro users only.
                            <Button component={Link} to="/pricing" size="small">
                              Upgrade
                            </Button>
                          </Alert>
                        )}
                        {(!remixCreation || !remixCreation.data) && (
                          <>
                            <Stack direction="column" spacing={1}>
                              <Typography variant="subtitle3">
                                Detail &amp; Complexity
                                <Tooltip
                                  title="Simple: Fewer details and subjects; Complex: More details and subjects - This option guides the AI to create a tattoo
                              design, but may not be exact."
                                >
                                  <IconButton size="small" sx={{ mt: -0.5 }}>
                                    <InfoIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              </Typography>
                              <ToggleButtonGroup
                                color="primary"
                                value={detailComplexity}
                                exclusive
                                onChange={(e, val) => {
                                  if (val !== null) {
                                    setDetailComplexity(val);
                                    onChangeParams({
                                      detail: val === 3 ? 3 : val,
                                      complexity: val === 1 ? 2 : val,
                                    });
                                  }
                                }}
                                aria-label="Detail"
                                disabled={!auth.isProUser}
                                size="small"
                                fullWidth
                              >
                                <ToggleButton value={1}>
                                  <MinorDetailIcon /> Simple
                                </ToggleButton>
                                <ToggleButton value={2}>
                                  <MediumDetailIcon /> Medium
                                </ToggleButton>
                                <ToggleButton value={3}>
                                  <MajorDetailIcon /> Complex
                                </ToggleButton>
                              </ToggleButtonGroup>
                            </Stack>

                            {[
                              "japanese",
                              "neotraditional",
                              "traditional",
                              "watercolor",
                              "new-school",
                              "surrealism",
                              "anime",
                              "realism",
                              "cyber-sigilism",
                              "abstract",
                              "color-infused-black-grey",
                              "3d",
                              "graffiti",
                              "biomechanical",
                            ].includes(formValues.style) && (
                              <Stack direction="column" spacing={1}>
                                <Typography variant="subtitle3">
                                  <ColorIcon sx={{ mb: -0.8 }} fontSize="small" />
                                  Colors
                                  <Tooltip title="Select colors to guide the AI in creating a tattoo design. The AI will use these colors as a reference.">
                                    <IconButton size="small" sx={{ mt: -0.5 }}>
                                      <InfoIcon fontSize="inherit" />
                                    </IconButton>
                                  </Tooltip>
                                </Typography>
                                <ColorPicker
                                  colors={formValues.params?.colors ?? []}
                                  disabled={!auth.isProUser}
                                  onChange={(colors) => {
                                    if (colors.length) {
                                      onChangeParams({
                                        colors,
                                        white_level: 0,
                                        black_level: 0,
                                      });
                                    } else {
                                      unsetParams([
                                        "colors",
                                        "white_level",
                                        "black_level",
                                      ]);
                                    }
                                  }}
                                />
                              </Stack>
                            )}

                            {["minimalist", "sketch", "ignorant", "stencil"].includes(
                              formValues.style,
                            ) && (
                              <Stack direction="column" spacing={1}>
                                <Typography variant="subtitle3">
                                  <LineWeightIcon sx={{ mb: -0.8 }} fontSize="small" />{" "}
                                  Line Weight
                                </Typography>
                                <ToggleButtonGroup
                                  color="primary"
                                  value={
                                    formValues.params?.inner_line_weight ??
                                    (formValues.style === "sketch" ? 1 : 3)
                                  }
                                  exclusive
                                  onChange={(e, val) => {
                                    if (val !== null) {
                                      onChangeParams({
                                        inner_line_weight: val,
                                        outer_line_weight: val,
                                      });
                                    }
                                  }}
                                  aria-label="Line Weight"
                                  disabled={!auth.isProUser}
                                  size="small"
                                  fullWidth
                                  sx={{
                                    "& .MuiToggleButton-root.Mui-disabled .MuiBox-root":
                                      {
                                        backgroundColor: "rgba(0, 0, 0, 0.26)",
                                      },
                                    "& .MuiToggleButton-root.Mui-selected .MuiBox-root":
                                      {
                                        backgroundColor: theme.palette.primary.main,
                                      },
                                  }}
                                >
                                  {[1, 3, 5].map((val, idx) => (
                                    <ToggleButton value={val} key={val}>
                                      <Box
                                        sx={{
                                          width: 32,
                                          height: 2 ** (idx + 1), // 2, 4, 8
                                          bgcolor:
                                            formValues.params?.inner_line_weight === val
                                              ? theme.palette.primary.main
                                              : "black",
                                        }}
                                      />
                                    </ToggleButton>
                                  ))}
                                </ToggleButtonGroup>
                              </Stack>
                            )}

                            <Stack direction="column" spacing={1}>
                              <Typography variant="subtitle3">
                                Modifiers
                                <Tooltip title="This helps the AI create tattoo designs based on your preferences.">
                                  <IconButton size="small" sx={{ mt: -0.5 }}>
                                    <InfoIcon fontSize="inherit" />
                                  </IconButton>
                                </Tooltip>
                              </Typography>
                              <ChipSelect
                                options={["masculine", "feminine", "symmetrical"]}
                                defaultOptions={modifiers}
                                onChange={setModifiers}
                                disabled={!auth.isProUser}
                              />
                            </Stack>
                          </>
                        )}
                        {remixCreation && remixCreation.data && (
                          <>
                            <Stack direction="column" spacing={1}>
                              <Typography variant="subtitle3">
                                <ModelTrainingIcon
                                  sx={{ mb: -0.8 }}
                                  fontSize="medium"
                                />{" "}
                                Remixing
                              </Typography>
                              <Button
                                variant="outlined"
                                onClick={() => {
                                  // redirect to create page
                                  navigate("/");
                                }}
                                startIcon={
                                  <CreationImage
                                    creation={remixCreation.data}
                                    indexToShow={remixIdx}
                                    size="small"
                                    showThumbnail
                                    lazy={false}
                                    fixedImageHeight={75}
                                  />
                                }
                                sx={{
                                  justifyContent: "space-between",
                                }}
                              >
                                <CloseIcon />
                              </Button>
                            </Stack>
                            <Stack direction="column" spacing={1}>
                              <Typography variant="subtitle3">
                                <DifferenceIcon sx={{ mb: -0.8 }} fontSize="medium" />{" "}
                                Amount of Change
                              </Typography>
                              <ToggleButtonGroup
                                color="primary"
                                value={formValues.params?.remix_strength ?? "medium"}
                                exclusive
                                onChange={(e, newValue) => {
                                  if (newValue !== null) {
                                    onChangeParams({ remix_strength: newValue });
                                  }
                                }}
                                aria-label="Amount of Change"
                                size="small"
                                fullWidth
                              >
                                <ToggleButton value="weak">Minimal</ToggleButton>
                                <ToggleButton value="medium">Some</ToggleButton>
                                <ToggleButton value="strong">Alot</ToggleButton>
                              </ToggleButtonGroup>
                            </Stack>
                          </>
                        )}
                        <Divider />
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="center"
                        >
                          <Typography variant="subtitle3" textAlign="right">
                            <VisibilityIcon sx={{ mb: -0.8 }} fontSize="medium" /> Keep
                            Private?
                          </Typography>
                          <Switch
                            checked={!formValues.public}
                            disabled={!auth.isProUser}
                            onChange={(e) => onChange("public", !e.target.checked)}
                          />
                        </Stack>
                      </Stack>
                    </AccordionDetails>
                  </Accordion>
                </Grid>
                <Grid item xs={12}>
                  <LoadingButton
                    variant="contained"
                    color="primary"
                    size="large"
                    type="submit"
                    loading={pending || (creation && creation.status === "IN_PROGRESS")}
                    disabled={creditsDepleted || anonCreditsDepleted}
                    loadingPosition="start"
                    startIcon={<DoubleArrowIcon />}
                    sx={{ my: 1, ...gradientGrainStyling }}
                    fullWidth
                  >
                    <span>{remixCreationId ? "Remix" : "Create"}</span>
                  </LoadingButton>
                </Grid>
              </Stack>
            </form>
          </Box>

          <Box mt={3} sx={{ display: { xs: "none", md: "block" } }}>
            <UpgradeBanner context="create" />
          </Box>
        </Grid>
        <Grid item xs={12} md={7} lg={8} ref={previewRef}>
          {creationReady ? (
            <Box>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="flex-end"
                sx={{ position: "relative" }}
              >
                <Button endIcon={<ShareIcon />} onClick={() => setOpenShareModal(true)}>
                  Share
                </Button>
                <SocialShareModal
                  open={openShareModal}
                  onClose={() => setOpenShareModal(false)}
                  creation={creation}
                  variant={variant}
                  mediaUrl={creation.imagePaths[0]}
                  linkUrl={`${window.location.protocol}//${
                    window.location.host
                  }${getCreationUrl(creation)}`}
                />
              </Box>
              <CreationDetailToolbar item={creation} variant={variant} />

              <CreationImage
                creation={creation}
                indexToShow={variant}
                responsive
                sx={{ py: 2 }}
              />
              <CreationDetailSelector
                item={creation}
                selected={variant}
                onClick={setVariant}
              />

              <CreationDetailTempBanner item={creation} variant={variant} />
            </Box>
          ) : (
            <Box>
              <Box
                display="flex"
                justifyContent="flex-end"
                alignItems="flex-end"
                sx={{ position: "relative" }}
              >
                <Button endIcon={<ShareIcon />} disabled>
                  Share
                </Button>
              </Box>
              <CreationDetailToolbar placeholder />
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  backgroundColor: "#f9f9f9",
                  border: "1px dashed #ccc",
                  borderRadius: 2,
                  my: 2,
                  maxHeight: 512,
                }}
              >
                {pending || (creation && creation.status === "IN_PROGRESS") ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      opacity: 0.7,
                      height: 512,
                    }}
                  >
                    <InkProgress size={375} />
                  </Box>
                ) : (
                  <img
                    src={`/images/hooded-reaper-tattoo-idea.webp`}
                    alt={"Example Tattoo Design"}
                    style={{
                      objectFit: "contain",
                      opacity: 0.2,
                      // grayscale and blur
                      filter: "grayscale(100%)",
                      width: "100%",
                      maxWidth: 512,
                      height: 480,
                      padding: 16,
                    }}
                    draggable={false}
                  />
                )}
              </Box>
              <CreationDetailSelector
                placeholder
                loading={pending || (creation && creation.status === "IN_PROGRESS")}
              />
            </Box>
          )}

          <Box my={3} sx={{ display: { xs: "block", md: "none" } }}>
            <UpgradeBanner context="create" />
          </Box>
        </Grid>
      </Grid>
    </Box>
  );
}

export default CreateDesignTool;
